import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { ConstraintChecker } from './pages/planning/ConstraintChecker';
import { PlanningApplications, PlanningApplicationDetail } from './pages/planning/Applications';
import { CommitteeAgendas, CommitteeAgendaDetail } from './pages/planning/Committee';
import { EnforcementCases, EnforcementCaseDetail } from './pages/planning/Enforcement';
import { HousingApplications, HousingApplicationDetail } from './pages/housing/Applications';
import { AllocationRounds, AllocationRoundDetail } from './pages/housing/Allocation';
import { Tenancies, TenancyDetail } from './pages/housing/Tenancies';
import { Assistance, AssistanceDetail } from './pages/housing/Assistance';
import { WorksPermits, WorksPermitDetail } from './pages/infrastructure/Permits';
import { DigOnceRegisterList, DigOnceDetail } from './pages/infrastructure/DigOnce';
import { DefectReports, DefectDetail } from './pages/infrastructure/Defects';
import { Contractors, ContractorDetail } from './pages/infrastructure/Contractors';
import { Projects, ProjectDetail } from './pages/infrastructure/Projects';
import { Shell } from './ui/Shell';

const PLANNING_ROLES = ['VALIDATION_OFFICER', 'PLANNER', 'SENIOR_PLANNER', 'COMMITTEE_SECRETARIAT', 'BUILDING_INSPECTOR', 'ENFORCEMENT'];
const HOUSING_ROLES = ['HOUSING_OFFICER', 'ALLOCATION_COMMITTEE', 'TENANCY_ADMIN'];
const INFRASTRUCTURE_ROLES = ['WORKS_OFFICER', 'ROADS_ENGINEER', 'PROJECT_PM'];

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // One shell navigating all three workspaces, not one app per role — land
  // each persona on the workspace their roles primarily belong to.
  const landing = PLANNING_ROLES.some((r) => persona.roles.includes(r))
    ? '/planning/applications'
    : HOUSING_ROLES.some((r) => persona.roles.includes(r))
      ? '/housing/applications'
      : INFRASTRUCTURE_ROLES.some((r) => persona.roles.includes(r))
        ? '/infrastructure/permits'
        : '/planning/constraint-checker';

  return (
    <Routes>
      <Route
        element={
          <Shell
            title="Land Use & Infrastructure"
            groups={[
              {
                label: 'Planning Authority',
                items: [
                  { to: '/planning/constraint-checker', label: 'Constraint Checker', icon: '⚑' },
                  { to: '/planning/applications', label: 'Applications', icon: '▤' },
                  { to: '/planning/committee', label: 'Committee', icon: '⚖' },
                  { to: '/planning/enforcement', label: 'Enforcement', icon: '⚠' },
                ],
              },
              {
                label: 'Housing Services',
                items: [
                  { to: '/housing/applications', label: 'Applications', icon: '⌂' },
                  { to: '/housing/allocation', label: 'Allocation & Units', icon: '⚷' },
                  { to: '/housing/tenancies', label: 'Tenancies', icon: '▥' },
                  { to: '/housing/assistance', label: 'Assistance', icon: '$' },
                ],
              },
              {
                label: 'Infrastructure & Public Works',
                items: [
                  { to: '/infrastructure/permits', label: 'Works Permits', icon: '⛏' },
                  { to: '/infrastructure/dig-once', label: 'Dig-Once Register', icon: '⌗' },
                  { to: '/infrastructure/defects', label: 'Defect Reports', icon: '▲' },
                  { to: '/infrastructure/contractors', label: 'Contractors', icon: '⚒' },
                  { to: '/infrastructure/projects', label: 'Projects', icon: '◫' },
                ],
              },
            ]}
          />
        }
      >
        <Route index element={<Navigate to={landing} replace />} />

        {/* Planning Authority (M17) */}
        <Route path="planning/constraint-checker" element={<ConstraintChecker />} />
        <Route path="planning/applications" element={<PlanningApplications />} />
        <Route path="planning/applications/:id" element={<PlanningApplicationDetail />} />
        <Route path="planning/committee" element={<CommitteeAgendas />} />
        <Route path="planning/committee/:id" element={<CommitteeAgendaDetail />} />
        <Route path="planning/enforcement" element={<EnforcementCases />} />
        <Route path="planning/enforcement/:id" element={<EnforcementCaseDetail />} />

        {/* Housing Services (M19) */}
        <Route path="housing/applications" element={<HousingApplications />} />
        <Route path="housing/applications/:id" element={<HousingApplicationDetail />} />
        <Route path="housing/allocation" element={<AllocationRounds />} />
        <Route path="housing/allocation/:id" element={<AllocationRoundDetail />} />
        <Route path="housing/tenancies" element={<Tenancies />} />
        <Route path="housing/tenancies/:id" element={<TenancyDetail />} />
        <Route path="housing/assistance" element={<Assistance />} />
        <Route path="housing/assistance/:id" element={<AssistanceDetail />} />

        {/* Infrastructure & Public Works (M20) */}
        <Route path="infrastructure/permits" element={<WorksPermits />} />
        <Route path="infrastructure/permits/:id" element={<WorksPermitDetail />} />
        <Route path="infrastructure/dig-once" element={<DigOnceRegisterList />} />
        <Route path="infrastructure/dig-once/:id" element={<DigOnceDetail />} />
        <Route path="infrastructure/defects" element={<DefectReports />} />
        <Route path="infrastructure/defects/:id" element={<DefectDetail />} />
        <Route path="infrastructure/contractors" element={<Contractors />} />
        <Route path="infrastructure/contractors/:id" element={<ContractorDetail />} />
        <Route path="infrastructure/projects" element={<Projects />} />
        <Route path="infrastructure/projects/:id" element={<ProjectDetail />} />
      </Route>

      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}
